import React, { useEffect, useState } from "react";

import { Box, Button, Typography, Card, CardContent, Grid, Container } from "@mui/material";
import { WISE_MEMO_WEB_ADDRESS } from "../../common/MetaSetting";
import { useTranslation } from "react-i18next";

export const Dashboard = () => {
  const { t, i18n } = useTranslation();
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    // 模拟页面加载动画
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 300);
    
    return () => clearTimeout(timer);
  }, []);

  const features = [
    {
      icon: "📝",
      title: t("feature1Title"),
      description: t("feature1Description")
    },
    {
      icon: "🔍", 
      title: t("feature2Title"),
      description: t("feature2Description")
    },
    {
      icon: "📱",
      title: t("feature3Title"), 
      description: t("feature3Description")
    }
  ];

  const benefits = [
    t("benefit1"),
    t("benefit2"),
    t("benefit3"),
    t("benefit4")
  ];

  return (
    <Box
      sx={{
        width: "100%",
        minHeight: "100vh",
        padding: { xs: "20px 0", md: "40px 0" }
      }}
    >
      {/* Hero Section */}
      <Container maxWidth="lg">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            textAlign: "center",
            padding: { xs: "60px 24px", md: "100px 24px" },
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? "translateY(0)" : "translateY(20px)",
            transition: "all 0.6s ease-out",
          }}
        >
          {/* Logo */}
          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: "50%",
              background: "linear-gradient(135deg, #FB4E2B 0%, #FF6B6B 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              marginBottom: "32px",
              boxShadow: "0 8px 32px rgba(251, 78, 43, 0.3)",
              animation: "float 3s ease-in-out infinite",
              '@keyframes float': {
                '0%, 100%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-10px)' }
              }
            }}
          >
            <Box
              component="img"
              src="/logo.svg"
              sx={{
                width: 60,
                height: 60,
                filter: "brightness(0) invert(1)"
              }}
            />
          </Box>
          
          {/* Main Title */}
          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #FB4E2B 0%, #FF6B6B 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              marginBottom: "16px",
              fontSize: { xs: "2.5rem", md: "3.5rem" }
            }}
          >
            {t("welcomeString")}
          </Typography>
          
          {/* Description */}
          <Typography
            variant="h5"
            sx={{
              color: "text.secondary",
              marginBottom: "40px",
              lineHeight: 1.6,
              fontSize: { xs: "1.1rem", md: "1.5rem" },
              maxWidth: "600px"
            }}
          >
            {t("welcomeDescription")}
          </Typography>
          
          {/* CTA Button */}
          <Button 
            href={WISE_MEMO_WEB_ADDRESS} 
            variant="contained" 
            size="large"
            sx={{
              background: "linear-gradient(135deg, #FB4E2B 0%, #FF6B6B 100%)",
              padding: "16px 40px",
              borderRadius: "12px",
              fontSize: "1.2rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 16px rgba(251, 78, 43, 0.3)",
              transition: "all 0.3s ease",
              '&:hover': {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(251, 78, 43, 0.4)",
                background: "linear-gradient(135deg, #FF6B6B 0%, #FB4E2B 100%)"
              }
            }}
          >
            {t("buttonStartToUser")}
          </Button>
        </Box>

        {/* Features Section */}
        <Box sx={{ padding: { xs: "60px 0", md: "80px 0" } }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              marginBottom: "16px",
              color: "text.primary"
            }}
          >
            {t("featureTitle")}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              textAlign: "center",
              color: "text.secondary",
              marginBottom: "60px",
              maxWidth: "500px",
              margin: "0 auto 60px"
            }}
          >
            {t("featureSubtitle")}
          </Typography>
          
          <Grid container spacing={4}>
            {features.map((feature, index) => (
              <Grid item xs={12} md={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    background: "rgba(255, 255, 255, 0.8)",
                    backdropFilter: "blur(10px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    borderRadius: "16px",
                    padding: "32px",
                    textAlign: "center",
                    transition: "all 0.3s ease",
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? "translateY(0)" : "translateY(20px)",
                    transitionDelay: isLoaded ? `${index * 0.1}s` : "0s",
                    '&:hover': {
                      transform: "translateY(-8px)",
                      boxShadow: "0 16px 40px rgba(0, 0, 0, 0.1)"
                    }
                  }}
                >
                  <CardContent>
                    <Typography variant="h2" sx={{ marginBottom: "16px" }}>
                      {feature.icon}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 600, marginBottom: "16px" }}>
                      {feature.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Benefits Section */}
        <Box sx={{ 
          padding: { xs: "60px 0", md: "80px 0" }, 
          background: "rgba(255, 255, 255, 0.5)", 
          borderRadius: "16px", // 添加圆角
          margin: "0" // 移除左右边距，与上下div保持一致
        }}>
          <Typography
            variant="h3"
            sx={{
              textAlign: "center",
              fontWeight: 700,
              marginBottom: "60px",
              color: "text.primary"
            }}
          >
            {t("benefitsTitle")}
          </Typography>
          
          <Grid container spacing={3}>
            {benefits.map((benefit, index) => (
              <Grid item xs={12} sm={6} key={index}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    padding: "16px 24px", // 增加左右内边距
                    background: "white",
                    borderRadius: "12px",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)",
                    opacity: isLoaded ? 1 : 0,
                    transform: isLoaded ? "translateX(0)" : "translateX(-20px)",
                    transition: `all 0.5s ease ${index * 0.1}s`,
                    margin: { xs: "0 8px", md: "0 16px" } // 为每个DIV块添加左右外边距
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "linear-gradient(135deg, #FB4E2B 0%, #FF6B6B 100%)",
                      marginRight: "16px"
                    }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 500 }}>
                    {benefit}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Final CTA Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #FB4E2B 0%, #FF6B6B 100%)",
            borderRadius: "24px",
            padding: { xs: "60px 24px", md: "80px 24px" },
            textAlign: "center",
            color: "white",
            marginTop: "40px"
          }}
        >
          <Typography variant="h3" sx={{ fontWeight: 700, marginBottom: "16px" }}>
            {t("ctaTitle")}
          </Typography>
          <Typography variant="h6" sx={{ marginBottom: "32px", opacity: 0.9 }}>
            {t("ctaDescription")}
          </Typography>
          <Button 
            href={WISE_MEMO_WEB_ADDRESS} 
            variant="contained" 
            size="large"
            sx={{
              background: "white",
              color: "#FB4E2B",
              padding: "16px 40px",
              borderRadius: "12px",
              fontSize: "1.2rem",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.2)",
              transition: "all 0.3s ease",
              '&:hover': {
                transform: "translateY(-2px)",
                boxShadow: "0 8px 24px rgba(0, 0, 0, 0.3)",
                background: "rgba(255, 255, 255, 0.95)"
              }
            }}
          >
            {t("buttonStartToUser")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default Dashboard;
